import React, { useMemo } from "react";
import Grid2 from "@mui/material/Unstable_Grid2";
import MuiSelect from "../../framework/muiSelect";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../framework/customStore";
import { GroupingValue } from "../../../store/reportOptionsSlice";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

import { Base } from "../../../framework/base";
import { AnyAction } from "@reduxjs/toolkit";
import { ReportButton } from "../ReportSection";

export enum GroupingMode {
    Single = "single",
    Multiple = "multiple",
}

interface Props {
    selectedGrouping: string[];
    groupByRow?: boolean;
    options: GroupingValue[];
    setGrouping: (val: string[]) => AnyAction;
    setGroupByRow?: (val: boolean) => AnyAction;
    enableGroupingModeSelection?: boolean;
    groupingModeMultiple?: boolean;
    setGroupingModeMultiple?: (val: boolean) => AnyAction;
    enableShowGroupedRowsSelection?: boolean;
    showGroupedRows?: boolean;
    setShowGroupedRows?: (val: boolean) => AnyAction;
    firstGroupingNoSelectionLabel?: string;
}

export const ReportGroupingPicker = ({
    selectedGrouping,
    groupByRow,
    options,
    setGrouping,
    setGroupByRow,
    enableGroupingModeSelection = false,
    groupingModeMultiple = false,
    setGroupingModeMultiple,
    enableShowGroupedRowsSelection = false,
    showGroupedRows = false,
    setShowGroupedRows,
    firstGroupingNoSelectionLabel,
}: Props) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    // Grouping options array define availabe groupings in this report
    const groupingOptions = useMemo(
        () =>
            [
                {
                    value: GroupingValue.Vehicle,
                    label: t("reporting.fleet"),
                    allowFirst: true,
                },
                {
                    value: GroupingValue.Employee,
                    label: t("reporting.employee"),
                    allowFirst: true,
                },
                {
                    value: GroupingValue.Customer,
                    label: t("reporting.customer"),
                    allowFirst: true,
                },
                {
                    value: GroupingValue.CostCenter,
                    label: t("reporting.costCenter"),
                    allowFirst: true,
                },
                {
                    value: GroupingValue.WorkTimeType,
                    label: t("reporting.workTimeType"),
                    allowFirst: true,
                },
                {
                    value: GroupingValue.Date,
                    label: t("reporting.date"),
                    allowFirst: true,
                },
                {
                    value: GroupingValue.WorkShift,
                    label: t("reporting.workShift"),
                    allowFirst: false,
                },
                {
                    value: GroupingValue.Geofence,
                    label: t("reporting.geofenceName"),
                    allowFirst: true,
                },
            ]
                .filter((o) => options.includes(o.value))
                .sort((a, b) => Base.stringCompare(a.label, b.label)),
        [options]
    );

    const onlyOneGroupingField = useMemo(
        () => !groupByRow && selectedGrouping?.length === 1,
        [groupByRow, selectedGrouping]
    );

    return (
        <Grid2 container spacing={2}>
            <GroupingColumn>
                <MuiSelect
                    size="small"
                    label={t("reporting.groupBy")}
                    value={selectedGrouping[0] ?? ""}
                    onChange={(val) =>
                        dispatch(
                            val
                                ? setGrouping(
                                    Base.getUniqueStringItems(
                                        [
                                            val,
                                            ...selectedGrouping.slice(1),
                                        ].filter(Boolean)
                                    )
                                )
                                : setGrouping([])
                        )
                    }
                    options={groupingOptions.filter((o) => o.allowFirst)}
                    disabled={
                        !firstGroupingNoSelectionLabel &&
                        selectedGrouping.length >= 2
                    }
                    {...(firstGroupingNoSelectionLabel
                        ? {
                            displayNoSelectionLabel: true,
                            noSelectionLabel: firstGroupingNoSelectionLabel,
                            enableClearButton: true,
                        }
                        : {})}
                />
            </GroupingColumn>

            {/*  Additional grouping selectors for nested grouping */}
            {selectedGrouping.map(
                (g, i) =>
                    groupingOptions.length > i + 1 && (
                        <GroupingColumn key={g}>
                            <MuiSelect
                                size="small"
                                label={t("reporting.groupBy")}
                                noSelectionLabel={t("reporting.noSelection")}
                                displayNoSelectionLabel
                                value={selectedGrouping[i + 1] ?? ""}
                                onChange={(val) =>
                                    dispatch(
                                        setGrouping([
                                            ...selectedGrouping.slice(0, i + 1),
                                            val,
                                        ])
                                    )
                                }
                                options={groupingOptions.filter(
                                    (o) =>
                                        !selectedGrouping.includes(o.value) ||
                                        selectedGrouping.indexOf(o.value) >=
                                            i + 1
                                )}
                                enableClearButton
                            />
                        </GroupingColumn>
                    )
            )}

            {!!setGroupByRow ? (
                <Grid2 xs={"auto"}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={groupByRow}
                                onChange={(_, checked) =>
                                    dispatch(setGroupByRow(checked))
                                }
                            />
                        }
                        label={t("reporting.rowGrouping")}
                    />
                    {enableShowGroupedRowsSelection ? (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={showGroupedRows}
                                    onChange={(_, checked) =>
                                        dispatch(setShowGroupedRows(checked))
                                    }
                                    disabled={onlyOneGroupingField}
                                />
                            }
                            label={t("reporting.showGroupedRows")}
                        />
                    ) : null}
                    {enableGroupingModeSelection ? (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    disabled={
                                        !showGroupedRows || onlyOneGroupingField
                                    }
                                    size="small"
                                    checked={
                                        !showGroupedRows ||
                                        onlyOneGroupingField ||
                                        groupingModeMultiple
                                    }
                                    onChange={(_, checked) =>
                                        dispatch(
                                            setGroupingModeMultiple(checked)
                                        )
                                    }
                                />
                            }
                            label={t("reporting.groupingModeMultiple")}
                        />
                    ) : null}
                </Grid2>
            ) : null}

            <Grid2 xsOffset="auto" alignItems="self-end">
                {selectedGrouping && (
                    <ReportButton
                        onClick={() => {
                            // Reverts back to first grouping selection
                            dispatch(setGrouping([selectedGrouping[0]]));

                            if (!!setGroupByRow) {
                                dispatch(setGroupByRow(false));
                            }
                        }}
                    >
                        {t("common.clear")}
                    </ReportButton>
                )}
            </Grid2>
        </Grid2>
    );
};

const GroupingColumn = ({ children }: { children: JSX.Element }) => (
    <Grid2 xs={12} sm={6} md={4} lg={3} xl={3} sx={{ minWidth: 260 }}>
        {children}
    </Grid2>
);
